import { Link, useParams } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import { PageHeader } from '@/components/layout/PageHeader';
import { Card, CardContent, CardHeader } from '@/components/ui/Card';
import { Badge, StatusBadge } from '@/components/ui/Badge';
import { EmptyState } from '@/components/ui/EmptyState';
import { Skeleton } from '@/components/ui/Skeleton';
import { useContact, useContactOutboundPushes } from '@/hooks/useDispatcher';
import { formatDateTime } from '@/lib/utils';

export default function ContactDetailPage() {
  const { id } = useParams<{ id: string }>();
  const contact = useContact(id);
  const pushes = useContactOutboundPushes(id);

  return (
    <div>
      <Link
        to="/contacts"
        className="text-xs text-gray-500 hover:text-brand-dark inline-flex items-center gap-1 mb-3"
      >
        <ArrowLeft size={12} /> Retour aux contacts
      </Link>
      <PageHeader
        title={contact.data?.company_name ?? '…'}
        description={contact.data?.email ?? contact.data?.domain ?? undefined}
      />

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-6">
        <Card className="lg:col-span-2">
          <CardHeader title="Identité" />
          <CardContent className="space-y-2 text-sm">
            <Field label="Nom complet" value={`${contact.data?.first_name ?? ''} ${contact.data?.last_name ?? ''}`.trim() || '—'} />
            <Field label="Email" value={contact.data?.email ?? '—'} />
            <Field label="Téléphone" value={contact.data?.phone ?? '—'} />
            <Field label="Poste" value={contact.data?.position ?? '—'} />
            <Field label="Industrie" value={contact.data?.industry ?? '—'} />
            <Field label="Site web" value={contact.data?.company_website ?? '—'} />
            <Field label="Domaine" value={contact.data?.domain ?? '—'} />
            <Field label="HubSpot ID" value={contact.data?.hubspot_contact_id ?? '—'} />
          </CardContent>
        </Card>

        <Card>
          <CardHeader title="Provenance" />
          <CardContent className="space-y-2 text-sm">
            <Field label="Batch source" value={contact.data?.source_batch_id ?? '—'} />
            <Field label="Job source" value={contact.data?.source_job_id ?? '—'} />
            <Field label="Créé" value={formatDateTime(contact.data?.created_at)} />
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardHeader
          title="Historique outbound_pushes"
          subtitle={`${pushes.data?.length ?? 0} pushes`}
        />
        {pushes.isLoading ? (
          <CardContent>
            <Skeleton className="h-32 w-full" />
          </CardContent>
        ) : !pushes.data?.length ? (
          <EmptyState title="Aucun push enregistré" />
        ) : (
          <div className="divide-y divide-brand-border">
            {pushes.data.map((p) => (
              <div key={p.id} className="flex items-center justify-between px-5 py-3">
                <div className="flex items-center gap-3">
                  <Badge tone="brand">{p.target}</Badge>
                  <span className="text-sm text-brand-dark">{p.target_ref ?? '—'}</span>
                </div>
                <div className="flex items-center gap-3">
                  <StatusBadge status={p.status} />
                  <span className="text-xs text-gray-400">
                    {formatDateTime(p.sent_at ?? p.created_at)}
                  </span>
                </div>
              </div>
            ))}
          </div>
        )}
      </Card>
    </div>
  );
}

function Field({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex items-start gap-4">
      <span className="w-28 shrink-0 text-xs text-gray-400">{label}</span>
      <span className="flex-1 text-brand-dark break-all">{value}</span>
    </div>
  );
}
